import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { LogOut, Moon, Sun } from 'lucide-react'
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@adsp/ui'
import { useAuthStore } from '@/stores/authStore'
import { navItems } from './nav'

const THEME_KEY = 'adsp.theme'
type Theme = 'light' | 'dark'

function readTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const toggle = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // ignore write failures (private mode, etc.)
    }
  }, [theme])
  return { theme, toggle }
}

export function AppShell() {
  const { t } = useTranslation('common')

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside className="flex w-[220px] shrink-0 flex-col border-r border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3.5">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-[13px] font-semibold">
            A
          </span>
          <span className="text-sm font-semibold tracking-tight">{t('brand')}</span>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
          {navItems.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-150 ease-out-expo',
                  isActive
                    ? 'bg-primary/10 font-medium text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="flex flex-col gap-0.5 border-t border-border p-2">
          <ThemeToggle />
          <UserMenu />
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}

function ThemeToggle() {
  const { t } = useTranslation('common')
  const { theme, toggle } = useTheme()
  const label = theme === 'dark' ? t('theme.light') : t('theme.dark')

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
    >
      {theme === 'dark' ? (
        <Sun className="h-4 w-4 shrink-0" />
      ) : (
        <Moon className="h-4 w-4 shrink-0" />
      )}
      {label}
    </button>
  )
}

function UserMenu() {
  const { t } = useTranslation('common')
  const { t: tAuth } = useTranslation('auth')
  const navigate = useNavigate()
  const admin = useAuthStore((s) => s.admin)
  const logout = useAuthStore((s) => s.logout)

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 truncate rounded-md px-2.5 py-1.5 text-left text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card data-[state=open]:bg-accent data-[state=open]:text-foreground"
        >
          <span className="truncate">{admin?.email ?? '—'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-52">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="truncate text-sm">{admin?.email}</span>
          {admin?.role && (
            <Badge tone="neutral" className="w-fit">
              {admin.role === 'super_admin' ? t('role.superAdmin') : t('role.siteAdmin')}
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {tAuth('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
